// src/components/resumo/annotations/NoteQuizModal.tsx
// 🎯 Modal de revisão de notas — Identidade GRAIA completa

import React, { useState } from 'react';
import {
    X,
    ChevronLeft,
    ChevronRight,
    Eye,
    EyeOff,
    Star,
    BookOpen,
    HelpCircle,
    Lightbulb,
    FileText,
    CheckCircle2,
    Trophy
} from 'lucide-react';
import { Note } from './annotations.types';
import './NoteQuizModal.css';

interface NoteQuizModalProps {
    notes: Note[];
    onClose: () => void;
    darkMode?: boolean;
}

const categoryConfig = {
    importante: { icon: Star, label: 'Importante', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
    revisar: { icon: BookOpen, label: 'Revisar', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
    duvida: { icon: HelpCircle, label: 'Dúvida', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
    conceito: { icon: Lightbulb, label: 'Conceito', color: '#5FBA61', bg: 'rgba(95,186,97,0.12)' },
};

const NoteQuizModal: React.FC<NoteQuizModalProps> = ({
    notes,
    onClose,
    darkMode = false
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showAnswer, setShowAnswer] = useState(false);
    const [finished, setFinished] = useState(false);

    const currentNote = notes[currentIndex];
    const progress = ((currentIndex + 1) / notes.length) * 100;
    const cat = currentNote ? categoryConfig[currentNote.category] : null;
    const CatIcon = cat?.icon ?? FileText;

    const handleNext = () => {
        if (currentIndex < notes.length - 1) {
            setCurrentIndex(i => i + 1);
            setShowAnswer(false);
        } else {
            setFinished(true);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(i => i - 1);
            setShowAnswer(false);
        }
    };

    if (!currentNote && !finished) return null;

    return (
        <div
            className={`nqm-overlay ${darkMode ? 'nqm-dark' : ''}`}
            onClick={onClose}
        >
            <div
                className="nqm-modal"
                onClick={e => e.stopPropagation()}
            >
                {/* ── TOPO DECORATIVO ── */}
                <div className="nqm-top-bar">
                    <div className="nqm-progress-track">
                        <div
                            className="nqm-progress-fill"
                            style={{ width: `${finished ? 100 : progress}%` }}
                        />
                    </div>
                </div>

                {/* ── HEADER ── */}
                <div className="nqm-header">
                    <div className="nqm-header-left">
                        <BookOpen size={18} className="nqm-header-icon" />
                        <span className="nqm-header-title">Revisão de Notas</span>
                    </div>
                    <div className="nqm-header-right">
                        {!finished && (
                            <span className="nqm-counter">
                                {currentIndex + 1}
                                <span className="nqm-counter-sep">/</span>
                                {notes.length}
                            </span>
                        )}
                        <button className="nqm-close-btn" onClick={onClose} title="Fechar">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* ── BODY ── */}
                {finished ? (
                    /* ── TELA FINAL ── */
                    <div className="nqm-finish">
                        <div className="nqm-finish-icon">
                            <Trophy size={48} />
                        </div>
                        <h2 className="nqm-finish-title">Revisão concluída!</h2>
                        <p className="nqm-finish-sub">
                            Você revisou <strong>{notes.length}</strong> nota{notes.length !== 1 ? 's' : ''} com sucesso.
                        </p>
                        <button className="nqm-btn-primary" onClick={onClose}>
                            <CheckCircle2 size={16} />
                            Fechar
                        </button>
                    </div>
                ) : (
                    <div className="nqm-body">
                        {/* Categoria */}
                        <div
                            className="nqm-category-badge"
                            style={{ color: cat?.color, background: cat?.bg, borderColor: cat?.color + '40' }}
                        >
                            <CatIcon size={13} />
                            <span>{cat?.label}</span>
                        </div>

                        {/* Pergunta */}
                        <div className="nqm-block nqm-block-question">
                            <span className="nqm-block-label">Pergunta</span>
                            <p className="nqm-block-text">{currentNote.question}</p>
                        </div>

                        {/* Revelar / Resposta */}
                        {!showAnswer ? (
                            <button
                                className="nqm-reveal-btn"
                                onClick={() => setShowAnswer(true)}
                            >
                                <Eye size={16} />
                                Revelar resposta
                            </button>
                        ) : (
                            <div className="nqm-answer-wrapper">
                                <div className="nqm-block nqm-block-answer">
                                    <div className="nqm-block-label-row">
                                        <span className="nqm-block-label">Resposta</span>
                                        <button
                                            className="nqm-hide-btn"
                                            onClick={() => setShowAnswer(false)}
                                        >
                                            <EyeOff size={12} />
                                            Ocultar
                                        </button>
                                    </div>
                                    <p className="nqm-block-text">{currentNote.answer}</p>
                                </div>

                                {currentNote.text && (
                                    <div className="nqm-original-note">
                                        <FileText size={13} className="nqm-original-icon" />
                                        <div>
                                            <span className="nqm-original-label">Nota original</span>
                                            <p className="nqm-original-text">{currentNote.text}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ── FOOTER ── */}
                {!finished && (
                    <div className="nqm-footer">
                        <button
                            className="nqm-nav-btn"
                            onClick={handlePrev}
                            disabled={currentIndex === 0}
                        >
                            <ChevronLeft size={16} />
                            Anterior
                        </button>

                        <div className="nqm-dots">
                            {notes.map((_, i) => (
                                <span
                                    key={i}
                                    className={`nqm-dot ${i === currentIndex ? 'nqm-dot-active' : ''} ${i < currentIndex ? 'nqm-dot-done' : ''}`}
                                />
                            ))}
                        </div>

                        <button
                            className="nqm-btn-primary"
                            onClick={handleNext}
                        >
                            {currentIndex < notes.length - 1 ? (
                                <>Próxima <ChevronRight size={16} /></>
                            ) : (
                                <>Finalizar <CheckCircle2 size={16} /></>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NoteQuizModal;